import { createApi } from '@reduxjs/toolkit/query/react';
import { CACHE_DURATIONS } from './baseApi';
import { axiosBaseQuery } from './axiosBaseQuery';
import type {
  CompanyEmployee,
  CompanyIntentProfile,
  CompanyOfficePresence,
  CompanyProduct,
  CompanyProfile,
  CompanyTechnology,
} from '@/lib/types/company-profile.types';

/**
 * Company profile API for `/company-details/[id]`.
 *
 * Talks to the Express proxy at `/api/company-profile` — see
 * `packages/server/api/company-profile` and
 * `packages/server/config/company-profile-service.ts`. That server-side client
 * proxies the real Entity Profile service (`companies/{id}` and
 * `companies/{id}/contacts`); fields with no upstream source (funding rounds,
 * acquisitions, technology spend) come back as real empty structures rather
 * than fabricated values, so those tabs render an empty state instead of mock
 * data.
 *
 * Uses `axiosBaseQuery` rather than `createBaseApi` because `createBaseApi`'s
 * default base query is a mock that answers `[]` to everything. Going through
 * axios also picks up the session-refresh and disabled-account handling in
 * `lib/axios`, which these authenticated routes need.
 */

const COMPANY_PROFILE_TAG = 'CompanyProfile' as const;

/**
 * Per-seniority headcounts the upstream computes across the WHOLE company,
 * not just the current page - confirmed live (2026-09-07): Transguard Group
 * returns {cLevel:4, vpLevel:0, director:10, manager:943} against a total of
 * 6788 contacts. `null` when the upstream request failed.
 */
export interface CompanySeniorityCounts {
  cLevel: number;
  vpLevel: number;
  director: number;
  manager: number;
}

/** Paged slice of employees, so the tab can page without refetching everything. */
export interface CompanyEmployeesPage {
  employees: CompanyEmployee[];
  total: number;
  seniorityCounts: CompanySeniorityCounts | null;
  /** The upstream Entity Profile contacts response, passed through untouched. */
  raw?: Record<string, unknown> | null;
}

export interface CompanyEmployeesArgs {
  companyId: string;
  page?: number;
  pageSize?: number;
  /**
   * Optional server-side seniority band filter - when set, the Entity Profile
   * Service returns only contacts in that bucket and `total` reflects the
   * filtered headcount. Named `seniorityName` to match the search service's
   * Omit (or pass "All") for every
   * contact.
   */
  seniorityName?: string;
}

/** Consolidated tech-profile response from a single upstream call. */
export interface CompanyTechProfile {
  technologies: CompanyTechnology[];
  products: CompanyProduct[];
  operatingSystems: string[];
  painpoints: string[];
  /** The upstream Entity Profile tech-profile response, passed through untouched. */
  raw?: Record<string, unknown>;
}

/** Shared cache-tag helper: every endpoint is scoped to one company id. */
function taggedByCompany(companyId: string) {
  return [{ type: COMPANY_PROFILE_TAG, id: companyId }];
}

export const companyProfileApi = createApi({
  reducerPath: 'companyProfileApi',
  baseQuery: axiosBaseQuery('/api/company-profile'),
  tagTypes: [COMPANY_PROFILE_TAG],
  keepUnusedDataFor: CACHE_DURATIONS.MEDIUM,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    /** The record behind the page header and the overview tab. */
    getCompanyProfile: builder.query<CompanyProfile, string>({
      query: (companyId) => ({ url: `/${encodeURIComponent(companyId)}` }),
      providesTags: (_result, _error, companyId) => taggedByCompany(companyId),
    }),

    getCompanyEmployees: builder.query<CompanyEmployeesPage, CompanyEmployeesArgs>({
      query: ({ companyId, page = 1, pageSize = 12, seniorityName }) => ({
        url: `/${encodeURIComponent(companyId)}/employees`,
        params: { page, pageSize, seniorityName },
      }),
      providesTags: (_result, _error, { companyId }) => taggedByCompany(companyId),
    }),

    /** Single call for technologies, products, and OS — one upstream hit. */
    getCompanyTechProfile: builder.query<CompanyTechProfile, string>({
      query: (companyId) => ({ url: `/${encodeURIComponent(companyId)}/tech-profile` }),
      providesTags: (_result, _error, companyId) => taggedByCompany(companyId),
    }),

    /** The Signals tab's intent panel. `null` = the company has no active intent. */
    getCompanyIntentProfile: builder.query<CompanyIntentProfile | null, string>({
      query: (companyId) => ({ url: `/${encodeURIComponent(companyId)}/intent-profile` }),
      providesTags: (_result, _error, companyId) => taggedByCompany(companyId),
    }),

    getCompanyLocations: builder.query<CompanyOfficePresence, string>({
      query: (companyId) => ({ url: `/${encodeURIComponent(companyId)}/locations` }),
      providesTags: (_result, _error, companyId) => taggedByCompany(companyId),
    }),

    /**
     * Get employee/contact IDs from a company for bulk selection.
     * Used for selecting multiple employees from the company details employees tab.
     * Returns only IDs for efficient cross-page selection.
     */
    getCompanyEmployeesSelection: builder.query<string[], { companyId: string; limit?: number; start?: number; seniorityName?: string }>({
      query: ({ companyId, limit = 25, start = 0, seniorityName }) => {
        const params: Record<string, string | number> = { limit, start };
        if (seniorityName && seniorityName !== 'All') {
          params.seniorityName = seniorityName;
        }
        return {
          url: `/${encodeURIComponent(companyId)}/contacts/selection`,
          params,
        };
      },
      keepUnusedDataFor: CACHE_DURATIONS.VERY_SHORT, // 1 minute - selection data is temporary
      transformResponse: (response: unknown): string[] => {
        // Response is array of contact IDs
        return Array.isArray(response) ? response.map(String) : [];
      },
      providesTags: (_result, _error, { companyId }) => taggedByCompany(companyId),
    }),
  }),
});

export const {
  useGetCompanyProfileQuery,
  useGetCompanyEmployeesQuery,
  useGetCompanyTechProfileQuery,
  useGetCompanyLocationsQuery,
  useGetCompanyIntentProfileQuery,
  useLazyGetCompanyEmployeesSelectionQuery,
} = companyProfileApi;
